import { AuthService } from './authService';
import { StcReservation, ReservationSummary, ReservationFilters } from '../types/reservation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ReservationService {
  /**
   * Gets the current company number from the authenticated user's token
   * @returns Company number
   * @throws Error if no company data is available
   */
  private getCompanyNo(): string {
    const userData = AuthService.getUserDataFromToken();
    if (!userData?.companyNo) {
      throw new Error('No company data available');
    }
    return userData.companyNo;
  }

  /**
   * Get all active reservations with optional filters
   * @param filters Optional filters for warehouse, item, bin code, and picking list
   * @returns Promise with array of reservation DTOs
   */
  async getActiveReservations(filters?: ReservationFilters): Promise<StcReservation[]> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();

    if (filters?.warehouseId && filters.warehouseId.trim() !== '') {
      params.append('warehouseId', filters.warehouseId);
    }
    if (filters?.itemNo && filters.itemNo.trim() !== '') {
      params.append('itemNo', filters.itemNo);
    }
    if (filters?.binCode && filters.binCode.trim() !== '') {
      params.append('binCode', filters.binCode);
    }
    if (filters?.pListId !== null && filters?.pListId !== undefined) {
      params.append('pListId', filters.pListId.toString());
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_BASE_URL}/${companyNo}/stcreservation?${queryString}`
      : `${API_BASE_URL}/${companyNo}/stcreservation`;

    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get active reservations: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Get a specific reservation by ID
   * @param resId Reservation ID
   * @returns Promise with reservation DTO
   */
  async getReservationById(resId: number): Promise<StcReservation> {
    const companyNo = this.getCompanyNo();

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stcreservation/${resId}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get reservation ${resId}: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Get summary statistics for active reservations
   * @returns Promise with reservation summary DTO
   */
  async getSummary(): Promise<ReservationSummary> {
    const companyNo = this.getCompanyNo();

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stcreservation/summary`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get reservation summary: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Allokering (TW 36362298): manuell frigivelse av en allokeringsreservasjon (resType 3).
   * Backend guarder på ResType 3 + aktiv status — andre reservasjoner avvises med 409.
   * @param resId Reservation ID
   */
  async releaseAllocation(resId: number): Promise<void> {
    const companyNo = this.getCompanyNo();

    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/stcreservation/${resId}/release-allocation`,
      {}
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Kunne ikke frigi allokeringsreservasjon ${resId}: ${response.status} ${response.statusText}. ${errorText}`);
    }
  }

  /**
   * Get all reservations for a specific picking list
   * @param pListId Picking list ID
   * @returns Promise with array of reservation DTOs
   */
  async getByPListId(pListId: number): Promise<StcReservation[]> {
    const companyNo = this.getCompanyNo();

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stcreservation/plist/${pListId}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get reservations for picking list ${pListId}: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }
}

export const reservationService = new ReservationService();
